import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Shield, Users, Clock, Smartphone, CheckCircle } from 'lucide-react';

const Home: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-pink-600/20"></div>
        <div className="relative container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center">
            {/* Header */}
            <div className="mb-12">
              <h1 className="text-6xl font-bold text-white mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Immersa VR Center
              </h1>
              <p className="text-xl text-slate-300 mb-8">
                Digital Waiver Management System
              </p>
            </div>

            {/* Main Action Card */}
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl shadow-2xl p-10 mb-12 border border-white/20">
              <div className="flex items-center justify-center mb-8">
                <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4 rounded-full">
                  <FileText className="w-12 h-12 text-white" />
                </div>
              </div>
              <h2 className="text-4xl font-bold text-white mb-6">
                Sign Your Digital Waiver
              </h2>
              <p className="text-slate-300 mb-10 text-xl leading-relaxed">
                New streamlined experience! Check existing waivers instantly<br/>
                or sign a new one in just a few clicks.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link
                  to="/waiver"
                  className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-10 py-5 rounded-2xl font-semibold text-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:-translate-y-2 hover:scale-105"
                >
                  <FileText className="w-6 h-6" />
                  Start Waiver Process
                </Link>
                <Link
                  to="/waiver/progressive"
                  className="inline-flex items-center gap-3 bg-white/10 border border-white/20 text-white px-6 py-3 rounded-xl font-medium hover:bg-white/20 transition-all duration-300 backdrop-blur-sm"
                >
                  Use Progressive Form
                </Link>
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/10 hover:bg-white/10 transition-all duration-300">
                <div className="bg-green-500/20 p-3 rounded-full w-fit mx-auto mb-6">
                  <Shield className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">
                  Secure & Safe
                </h3>
                <p className="text-slate-300">
                  Your information is encrypted and securely stored with industry-standard security
                </p>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/10 hover:bg-white/10 transition-all duration-300">
                <div className="bg-blue-500/20 p-3 rounded-full w-fit mx-auto mb-6">
                  <Users className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">
                  Family Friendly
                </h3>
                <p className="text-slate-300">
                  Sign waivers for yourself, family members, or group visits all in one go
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-white/10 hover:bg-white/10 transition-all duration-300">
                <div className="bg-purple-500/20 p-3 rounded-full w-fit mx-auto mb-6">
                  <Clock className="w-8 h-8 text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">
                  Fast & Easy
                </h3>
                <p className="text-slate-300">
                  Complete the entire process in under 2 minutes with our streamlined form
                </p>
              </div>
            </div>

            {/* Process Steps */}
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-10 border border-white/10">
              <h3 className="text-3xl font-bold text-white mb-10">How It Works</h3>
              <div className="grid md:grid-cols-4 gap-8">
                <div className="text-center">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl">1</div>
                  <h4 className="text-white font-semibold mb-2">Fill Form</h4>
                  <p className="text-slate-400 text-sm">Enter your personal information</p>
                </div>
                <div className="text-center">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl">2</div>
                  <h4 className="text-white font-semibold mb-2">Add Family</h4>
                  <p className="text-slate-400 text-sm">Include family members if needed</p>
                </div>
                <div className="text-center">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl">3</div>
                  <h4 className="text-white font-semibold mb-2">Sign Digitally</h4>
                  <p className="text-slate-400 text-sm">Provide your digital signature</p>
                </div>
                <div className="text-center">
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-white font-semibold mb-2">You're Ready!</h4>
                  <p className="text-slate-400 text-sm">Enjoy your VR experience</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black/20 backdrop-blur-sm border-t border-white/10 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <p className="text-slate-400">&copy; 2025-2026 Immersa VR Center. All rights reserved.</p>
            <div className="mt-4">
              <span className="inline-flex items-center gap-2 text-slate-400">
                <Smartphone className="w-4 h-4" />
                Mobile-friendly • Secure • Fast
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
